import { useState, useRef, useEffect, useCallback } from 'react'
import { Bot, Send, User, Key, AlertCircle, Sparkles, Plus, Trash2, Clock, ChevronRight, Info, Wrench } from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import api from '../lib/api'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  toolCalls?: string | null  // JSON string of tool names
  createdAt: string
  error?: boolean
}

interface Conversation {
  id: string
  title: string
  createdAt: string
  updatedAt: string
}

const TOOL_LABELS: Record<string, string> = {
  search_employees:      'Recherche employés',
  get_employee_details:  'Détails employé',
  get_absences:          'Absences',
  get_contract_endings:  'Fins de contrat',
  get_pending_signatures:'Signatures en attente',
  get_missing_data:      'Données manquantes',
  get_salary_distribution:'Répartition salariale',
  get_turnover_stats:    'Turnover',
  get_parity_stats:      'Parité H/F',
  get_absence_balances:  'Soldes congés',
}

const QUICK_PROMPTS = [
  'Quels employés ont un CDD se terminant dans les 30 prochains jours ?',
  'Montre-moi les demandes d\'absence en attente de validation.',
  'Quels dossiers employés sont incomplets (données manquantes) ?',
  'Donne-moi les statistiques de parité et l\'écart salarial H/F.',
  'Combien de feuilles d\'heures sont en attente de validation ?',
  'Quel est le taux de turnover cette année ?',
]

function renderMarkdown(text: string) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="bg-gray-100 text-purple-700 px-1 rounded text-xs font-mono">$1</code>')
    .replace(/^#{1,3} (.+)$/gm, '<p class="font-semibold text-gray-900 mt-3 mb-1">$1</p>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 list-decimal">$2</li>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>')
}

export default function Assistant() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeId, setActiveId]           = useState<string | null>(null)
  const [messages, setMessages]           = useState<Message[]>([])
  const [input, setInput]                 = useState('')
  const [loading, setLoading]             = useState(false)
  const [loadingConvs, setLoadingConvs]   = useState(true)
  const [needsKey, setNeedsKey]           = useState(false)
  const [sidebarOpen, setSidebarOpen]     = useState(true)
  const [bannerOpen, setBannerOpen]       = useState(true)
  const bottomRef = useRef<HTMLDivElement>(null)

  // ── Load conversations ─────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      const r = await api.get('/assistant/conversations')
      setConversations(r.data)
      if (r.data.length > 0 && !activeId) {
        setActiveId(r.data[0].id)
      }
    } catch { /* silently fail */ } finally {
      setLoadingConvs(false)
    }
  }, [activeId])

  useEffect(() => { loadConversations() }, [])

  // ── Load messages when conversation changes ────────────────────
  useEffect(() => {
    if (!activeId) { setMessages([]); return }
    api.get(`/assistant/conversations/${activeId}/messages`)
      .then(r => setMessages(r.data))
      .catch(() => setMessages([]))
  }, [activeId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  // ── Create new conversation ────────────────────────────────────
  async function createConversation() {
    const r = await api.post('/assistant/conversations')
    setConversations(prev => [r.data, ...prev])
    setActiveId(r.data.id)
    setMessages([])
    setInput('')
  }

  // ── Delete conversation ────────────────────────────────────────
  async function deleteConversation(id: string) {
    await api.delete(`/assistant/conversations/${id}`)
    const remaining = conversations.filter(c => c.id !== id)
    setConversations(remaining)
    if (activeId === id) {
      setActiveId(remaining[0]?.id || null)
    }
  }

  // ── Send message ──────────────────────────────────────────────
  async function send(text?: string) {
    const msg = (text || input).trim()
    if (!msg || loading) return
    setInput('')

    // Create conversation if none exists
    let convId = activeId
    if (!convId) {
      const r = await api.post('/assistant/conversations')
      convId = r.data.id
      setConversations(prev => [r.data, ...prev])
      setActiveId(convId)
    }

    // Optimistic user message
    const tempUser: Message = {
      id: `tmp-${Date.now()}`, role: 'user', content: msg, createdAt: new Date().toISOString()
    }
    setMessages(prev => [...prev, tempUser])
    setLoading(true)

    try {
      const r = await api.post(`/assistant/conversations/${convId}/messages`, { content: msg })
      // Replace temp + add assistant reply
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUser.id)
        return [...withoutTemp, { ...tempUser, id: `user-${Date.now()}` }, r.data.message]
      })
      // Refresh conversation list to update title + updatedAt
      loadConversations()
      setNeedsKey(false)
    } catch (err: any) {
      const errMsg = err.response?.data?.error || 'Erreur de connexion'
      if (errMsg.includes('ANTHROPIC_API_KEY') || errMsg.includes('invalide')) setNeedsKey(true)
      const errMsg2: Message = {
        id: `err-${Date.now()}`, role: 'assistant', content: errMsg,
        createdAt: new Date().toISOString(), error: true
      }
      setMessages(prev => prev.filter(m => m.id !== tempUser.id).concat(tempUser, errMsg2))
    } finally {
      setLoading(false)
    }
  }

  const activeConv = conversations.find(c => c.id === activeId)

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Sidebar ──────────────────────────────────────────────── */}
      {sidebarOpen && (
        <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col flex-shrink-0">
          <div className="p-3 border-b border-gray-200">
            <button
              onClick={createConversation}
              className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors">
              <Plus size={15} /> Nouvelle conversation
            </button>
          </div>

          <div className="flex-1 overflow-auto py-2">
            {loadingConvs ? (
              <p className="text-xs text-gray-400 text-center py-6">Chargement…</p>
            ) : conversations.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">Aucun historique</p>
            ) : (
              conversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => setActiveId(conv.id)}
                  className={`group relative mx-2 mb-1 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                    conv.id === activeId
                      ? 'bg-white shadow-sm border border-gray-200'
                      : 'hover:bg-white hover:shadow-sm'
                  }`}>
                  <div className="flex items-start gap-2">
                    <Clock size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-800 truncate leading-snug">{conv.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {format(new Date(conv.updatedAt), "d MMM 'à' HH:mm", { locale: fr })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); deleteConversation(conv.id) }}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 rounded transition-all">
                    <Trash2 size={11} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── Chat principal ────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-white flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(v => !v)}
            className="text-gray-400 hover:text-gray-600 transition-colors mr-1"
            title={sidebarOpen ? "Masquer l'historique" : "Afficher l'historique"}>
            <ChevronRight size={18} className={`transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-gray-900">Assistant RH</h1>
            <p className="text-xs text-gray-400 truncate">
              {activeConv ? activeConv.title : 'Posez une question RH — données en lecture seule'}
            </p>
          </div>
          <button
            onClick={() => setBannerOpen(v => !v)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Voir les limites de l'assistant">
            <Info size={16} />
          </button>
        </div>

        {/* Bannière clé API manquante */}
        {needsKey && (
          <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 flex-shrink-0">
            <Key size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-semibold text-amber-800">Clé API Anthropic requise</p>
              <p className="text-amber-700 mt-0.5">
                Ajoutez <code className="bg-amber-100 px-1 rounded">ANTHROPIC_API_KEY="sk-ant-..."</code> dans{' '}
                <code className="bg-amber-100 px-1 rounded">backend/.env</code> puis redémarrez le serveur.
              </p>
            </div>
          </div>
        )}

        {/* Bannière limites */}
        {bannerOpen && (
          <div className="mx-4 mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3 flex gap-2 flex-shrink-0">
            <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700 flex-1">
              <span className="font-semibold">Cet assistant est consultatif et en lecture seule.</span>
              {' '}Il peut interroger les données RH (employés, absences, contrats, soldes) mais{' '}
              <strong>ne modifie aucune donnée</strong>, ne gère pas le planning ni les pointages,
              et ne lit pas le contenu des documents.
            </div>
            <button onClick={() => setBannerOpen(false)} className="text-blue-400 hover:text-blue-600 flex-shrink-0 text-lg leading-none">×</button>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
          {messages.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center mb-4">
                <Sparkles size={24} className="text-purple-500" />
              </div>
              <p className="text-gray-700 font-medium mb-1">Que puis-je faire pour vous ?</p>
              <p className="text-gray-400 text-sm mb-6">Interrogez vos données RH en langage naturel</p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={msg.id || i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                msg.role === 'assistant'
                  ? 'bg-gradient-to-br from-purple-500 to-indigo-600'
                  : 'bg-blue-600'
              }`}>
                {msg.role === 'assistant'
                  ? <Bot size={15} className="text-white" />
                  : <User size={15} className="text-white" />
                }
              </div>
              <div className="flex flex-col gap-1 max-w-[75%]">
                {/* Tool badges */}
                {msg.role === 'assistant' && msg.toolCalls && (() => {
                  try {
                    const tools: string[] = JSON.parse(msg.toolCalls)
                    if (tools.length === 0) return null
                    return (
                      <div className="flex flex-wrap gap-1">
                        {[...new Set(tools)].map(t => (
                          <span key={t} className="inline-flex items-center gap-1 text-xs bg-purple-50 border border-purple-100 text-purple-600 rounded-full px-2 py-0.5">
                            <Wrench size={9} /> {TOOL_LABELS[t] || t}
                          </span>
                        ))}
                      </div>
                    )
                  } catch { return null }
                })()}
                <div className={`rounded-2xl px-4 py-3 text-sm ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white rounded-tr-sm'
                    : msg.error
                      ? 'bg-red-50 text-red-700 border border-red-100 rounded-tl-sm flex items-start gap-2'
                      : 'bg-white border border-gray-100 text-gray-800 rounded-tl-sm shadow-sm'
                }`}>
                  {msg.error && <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />}
                  {msg.role === 'assistant' && !msg.error ? (
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Bot size={15} className="text-white" />
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                <div className="flex gap-1.5 items-center h-5">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Suggestions rapides */}
        {messages.length === 0 && (
          <div className="px-4 pb-2 flex-shrink-0">
            <div className="flex flex-wrap gap-2">
              {QUICK_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => send(p)}
                  className="text-xs text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-100 rounded-full px-3 py-1.5 transition-colors text-left">
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="px-4 pb-4 flex-shrink-0">
          <div className="flex gap-2 bg-white border border-gray-200 rounded-xl shadow-sm p-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
              placeholder="Posez votre question RH…"
              disabled={loading}
              className="flex-1 text-sm px-2 py-1 focus:outline-none disabled:opacity-50 bg-transparent" />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="w-9 h-9 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg flex items-center justify-center transition-colors flex-shrink-0">
              <Send size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
